// Champ « ville » : recherche libre (français, anglais, alias, sans accent), liste de résultats,
// pays affiché pour trancher les homonymes. Le nom choisi est celui qui s’affiche ensuite.
import { searchZones, type Place } from '../core/places';
import { time } from '../core/time';
import { esc } from './dom';

export interface ZoneValue { tz: string; place: string; country: string }

export interface ZonePicker {
  value: ZoneValue | null;
  set(v: ZoneValue | null): void;
  focus(): void;
}

export function mountZonePicker(container: HTMLElement, opts: { id: string; initial: ZoneValue | null; onPick?: (v: ZoneValue) => void }): ZonePicker {
  container.classList.add('zone');
  container.innerHTML = `
    <input id="${esc(opts.id)}" autocomplete="off" role="combobox" aria-expanded="false" aria-autocomplete="list"
      aria-controls="${esc(opts.id)}-list" placeholder="Lyon, Mumbai, Genève, Hong Kong…" spellcheck="false">
    <div id="${esc(opts.id)}-list" class="zone-results" role="listbox" hidden></div>
    <small class="hint" data-hint>Tapez une ville, en français ou en anglais, puis choisissez dans la liste.</small>`;
  const input = container.querySelector<HTMLInputElement>('input')!;
  const list = container.querySelector<HTMLElement>('.zone-results')!;
  const hint = container.querySelector<HTMLElement>('[data-hint]')!;
  let value: ZoneValue | null = null;
  let results: Place[] = [];
  let active = -1;

  function show(q: string): void {
    results = searchZones(q);
    active = -1;
    list.innerHTML = results.length
      ? results.map((p, i) => `<button type="button" class="zone-option" role="option" id="${esc(opts.id)}-opt-${i}" aria-selected="false" data-i="${i}"><span>${esc(p.label)}</span><small>${esc(p.country)} · ${esc(p.tz)}</small></button>`).join('')
      : '<p class="zone-empty">Aucune ville trouvée. Essayez le nom du pays ou une autre orthographe.</p>';
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }
  function close(): void {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }
  function pick(p: Place): void {
    value = { tz: p.tz, place: p.label, country: p.country };
    input.value = `${p.label} · ${p.country}`;
    hint.textContent = `Il est ${time(p.tz, Date.now())} à ${p.label} (${p.tz}).`;
    close();
    opts.onPick?.(value);
  }
  function setActive(i: number): void {
    const buttons = [...list.querySelectorAll<HTMLElement>('.zone-option')];
    if (!buttons.length) return;
    active = Math.max(0, Math.min(buttons.length - 1, i));
    buttons.forEach((b, k) => b.setAttribute('aria-selected', String(k === active)));
    const b = buttons[active]!;
    input.setAttribute('aria-activedescendant', b.id);
    b.scrollIntoView({ block: 'nearest' });
  }

  input.addEventListener('input', () => { value = null; hint.textContent = 'Choisissez une ville dans la liste.'; show(input.value); });
  input.addEventListener('focus', () => show(value ? '' : input.value));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) show(input.value); setActive(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
    else if (e.key === 'Enter') { if (!list.hidden && results.length) { e.preventDefault(); pick(results[Math.max(0, active)]!); } }
    else if (e.key === 'Escape') { if (!list.hidden) { e.preventDefault(); e.stopPropagation(); close(); } }
  });
  list.addEventListener('mousedown', (e) => e.preventDefault()); // garde le focus dans le champ
  list.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLElement>('[data-i]');
    if (b) pick(results[Number(b.dataset.i)]!);
  });
  document.addEventListener('click', (e) => { if (!container.contains(e.target as Node)) close(); });

  const api: ZonePicker = {
    get value() { return value; },
    set(v) {
      value = v;
      if (v) { input.value = `${v.place} · ${v.country}`; hint.textContent = `Il est ${time(v.tz, Date.now())} à ${v.place} (${v.tz}).`; }
      else { input.value = ''; hint.textContent = 'Tapez une ville, en français ou en anglais, puis choisissez dans la liste.'; }
      close();
    },
    focus() { input.focus(); },
  };
  api.set(opts.initial);
  return api;
}
